import { IconArrowDown, IconPlus } from "@tabler/icons-react"
import { type FC, Fragment, useEffect, useState } from "react"

import type { LandingPageBlock } from "@forento/core/models/landing"
import { blockDefinition, landingPageBlockTypes } from "@forento/core/models/landingBlock"

import Button from "~/components/Button"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"

import BlockThumbnail from "./BlockThumbnail"

export type AddBlockRequest =
	| { mode: "choose-position"; type: LandingPageBlock["type"] }
	| { mode: "choose-type"; insertionIndex: number }

type Props = {
	request: AddBlockRequest | null
	blocks: LandingPageBlock[]
	pageLabel: string
	onAdd(type: LandingPageBlock["type"], insertionIndex: number): void
	onClose(): void
}
const AddBlockModal: FC<Props> = ({ request, blocks, pageLabel, onAdd, onClose }) => {
	const [lastRequest, setLastRequest] = useState<AddBlockRequest | null>(request)
	useEffect(() => {
		if (request !== null) setLastRequest(request)
	}, [request])
	const visibleRequest = request ?? lastRequest
	const isChoosingPosition = visibleRequest?.mode === "choose-position"
	const insertionIndex = visibleRequest?.mode === "choose-type" ? visibleRequest.insertionIndex : null

	return (
		<DefaultModal onClose={onClose} width={760} isOpen={request !== null}>
			<ModalTitle>{isChoosingPosition ? "Choose a position" : "Add a block here"}</ModalTitle>
			<ModalText>
				{isChoosingPosition && visibleRequest
					? `Choose where the ${blockDefinition(visibleRequest.type).label.toLowerCase()} block should appear on ${pageLabel}.`
					: insertionIndex !== null
						? `${getInsertionPositionLabel(blocks, insertionIndex)} on ${pageLabel}.`
						: "Choose a block to add to the page."}
			</ModalText>
			<ModalInputs>
				{isChoosingPosition && visibleRequest ? (
					<div className="max-h-[60vh] scroll-py-3 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-gray-50 p-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-300 hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-transparent">
						{Array.from({ length: blocks.length + 1 }, (_, insertionIndex) => (
							<Fragment key={insertionIndex}>
								<button
									type="button"
									onClick={() => onAdd(visibleRequest.type, insertionIndex)}
									className="group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-indigo-600 transition-colors hover:bg-indigo-50 focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none"
								>
									<span className="h-px flex-1 bg-indigo-200 transition-colors group-hover:bg-indigo-400" />
									<span className="flex shrink-0 items-center gap-2 rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-bold shadow-sm transition-all group-hover:border-indigo-400 group-hover:shadow-md">
										<IconPlus className="size-4" />
										{getInsertionPositionLabel(blocks, insertionIndex)}
									</span>
									<span className="h-px flex-1 bg-indigo-200 transition-colors group-hover:bg-indigo-400" />
								</button>
								{blocks[insertionIndex] && (
									<div className="mx-6 flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xs">
										<BlockThumbnail
											type={blocks[insertionIndex].type}
											className="w-20 shrink-0 rounded text-gray-500"
										/>
										<div className="min-w-0">
											<div className="text-sm font-bold text-gray-900">
												{blockDefinition(blocks[insertionIndex].type).label}
											</div>
											<div className="text-xs text-gray-500">Block {insertionIndex + 1}</div>
										</div>
										<IconArrowDown className="ml-auto size-4 text-gray-300" />
									</div>
								)}
							</Fragment>
						))}
					</div>
				) : (
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						{landingPageBlockTypes.map(type => (
							<button
								key={type}
								type="button"
								onClick={() => insertionIndex !== null && onAdd(type, insertionIndex)}
								className="group flex cursor-pointer flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 text-left text-sm font-semibold text-gray-700 transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 hover:shadow-md focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none"
							>
								<BlockThumbnail
									type={type}
									className="w-full rounded-lg text-gray-500 transition-colors group-hover:text-indigo-600"
								/>
								<span className="flex items-center gap-1.5">
									<IconPlus className="size-4" />
									{blockDefinition(type).label}
								</span>
							</button>
						))}
					</div>
				)}
			</ModalInputs>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

export function getInsertionPositionLabel(blocks: LandingPageBlock[], insertionIndex: number): string {
	if (insertionIndex <= 0) return "At the top"
	if (insertionIndex >= blocks.length) return "At the bottom"
	return `Between ${blockDefinition(blocks[insertionIndex - 1]!.type).label} and ${blockDefinition(blocks[insertionIndex]!.type).label}`
}

export default AddBlockModal
